import * as Sentry from '@sentry/browser'
import * as React from 'react'
import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { Link, Prompt, RouteComponentProps } from 'react-router'
import {
  Button,
  ButtonGroup,
  Callout,
  Card,
  Drawer,
  Elevation,
  FormGroup,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Tag,
  TextArea,
  Tooltip
} from '@blueprintjs/core'
import { connect } from 'react-redux'
import CX from 'classnames'
import { IconNames } from '@blueprintjs/icons'
import isEqual from 'lodash.isequal'
import uniq from 'lodash.uniq'
import { LoadableSelect } from '../../components/Inputs'
import { ExpressListCategorySelect } from './ExpressListCategorySelect'
import Client from '../../client'
import { showFailure, showToast } from '../../Toast'
import { getErrorString } from '../../utils/serverErrorUtils'
import './style.scss'
import { AppState } from '../../store'
import { CurrentState } from '../../current/types'
import { ExpressListPriceInput } from './ExpressListPriceInput'
import {
  applyDefaultBoxPadding,
  applyDefaultBoxPaddingIfDimensionsPresent,
  parseBoxPadding,
  renderErrorButPreferRequired,
  notFilledRequiredSpecifics
} from '../products_v5/components/JoinedForm/Helpers'
import { CategoryListingConfiguration, ListingStrategy, MarketSettings } from '../../types/api'
import {
  createEmptyExpressListProduct,
  ExpressListProductState,
  getExpressListFormSnapshot,
  getPreferredStrategy,
  safeIsEmpty
} from './utils'
import { SHOPGOODWILL_CHANNEL_ID, EBAY_CHANNEL_ID, PreservedListingOptions } from './types'
import { sgwFormReducer } from './sgwFormReducer'
import { ebayFormReducer } from './ebayFormReducer'
import {
  expressListMarketIds,
  findActiveCmc,
  hasEbayChannel,
  hasShopgoodwillChannel,
  marketChannelLabel
} from './channelHelpers'
import { computeEbayAutofill, emptyEbayRequirements, loadEbayRequirements } from './fetchEbayRequirements'
import { ExpressListEbayCard } from './ExpressListEbayCard'
import { DrawerSection, ExpressListDrawer } from './ExpressListDrawer'
import { DescriptionOrAttributes } from './inputs'
import {
  getProductAttrsFromTemplateForm,
  mergePreviousTemplateFormValues,
  setDraftProductValuesInTemplateForm,
  setNotesValuesInTemplateForm
} from '../products_v5/utils'
import compiler from '../admin/templates/builder/Engine'
import { TemplateFormItem } from '../products_v5/components/JoinedForm/types'
import config from '../../config'
import Uploader from '../../components/images/Uploader'
import TagSelector from '../product_manager/components/TagSelector'
import NotesRenderer from '../../components/Notes'
import { StrategyPicker } from './StrategyPicker'
import { ProgressBar } from './ProgressBar'
import { Timer } from './Timer'
import { useTimerSeconds } from './useTimerSeconds'
import { useScaleStreamer } from '../../printing/scale'
import { DimensionInput } from './__fixtures__/DimensionInput'
import { ShippingBoxSelect } from '../products_v5/components/JoinedForm/ProductForm/ShippingBoxSelect'
import { SvgIcon } from './SvgIcon'
import WeightInput from '../../printing/components/WeightInput'
import { tryPrintExpressListProductLabel } from './expressListPrint'
import { ScaleContext } from '../../printing/ScaleContext'
import { IndexedInventoryLocationSelect } from '../../components/inputs/IndexedInventoryLocationSelect'
import { stringToBool } from '../../utils/string'
import useRememberedState from '../../hooks/useRememberedState'

const AUTO_PRINT_STORAGE_KEY = 'autoPrintAfterExpressListSave'
const SIDE_DRAWER_FIELDS = [
  'listingStrategy',
  'buyNowPrice',
  'shippingPrice',
  'shippingMethod',
  'noCombineShipping',
  'featuredItem'
] as const

type SideDrawerField = (typeof SIDE_DRAWER_FIELDS)[number]
type SideDrawerValues = Record<SideDrawerField, unknown>

interface Props extends RouteComponentProps<{ id: string }> {
  current: CurrentState
  property: string
}

type FetchedProduct = ExpressListProductState

const supplierSelectOptions = (data: { suppliers: { id: number; name: string; allowedForStrategy?: boolean }[] }) =>
  data.suppliers.map(s => ({
    value: s.id,
    label: s.allowedForStrategy === false ? `${s.name} (Not allowed for the selected strategy)` : s.name,
    disabled: s.allowedForStrategy === false
  }))

const initializeTemplateState = (
  fetchedProduct: FetchedProduct,
  templates: any[],
  previousTemplateForm?: TemplateFormItem[]
) => {
  const template = templates[0]

  // It's possible this step isn't necessary, if we go with "Possible approach A".
  // Or maybe this is effectively the initialization of the template form with draft field values
  // (versus the "Possible approach A" that updates template values on-the-fly so the engine
  // will include width/etc in the title/description), TBD.
  const templateWithDraftProductValues = setDraftProductValuesInTemplateForm(fetchedProduct, template)

  const templateWithAppliedValues = setNotesValuesInTemplateForm(fetchedProduct.notes, templateWithDraftProductValues)

  if (previousTemplateForm?.length) {
    return mergePreviousTemplateFormValues(previousTemplateForm, templateWithAppliedValues)
  }

  return templateWithAppliedValues
}

const FORM_VERSION_INTEGER = 7 // JoinedForm is products v5. There was a prototype products_v6 that Jackson built. So ExpressList is version 7.
const FORM_VERSION_STRING = 'v7'

const ExpressListForm = (props: Props) => {
  const { id } = props.match.params
  const { uniqueConnectedMarkets } = props.current.organization
  const isNewProduct = id === 'new'
  const existingProductId = isNewProduct ? null : parseInt(id, 10)
  const organizationId = props.current.organization.id

  // Passed from ExpressListStart via react-router state (search result or empty new product).
  const fetchedProduct =
    (props.history.location.state as FetchedProduct) || (isNewProduct ? createEmptyExpressListProduct() : undefined)
  const skuFromDraft = !!fetchedProduct?.sku

  const [loadingCategoryConfiguration, setLoadingCategoryConfiguration] = useState(false)
  const [loadingStrategyConfiguration, setLoadingStrategyConfiguration] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [editingDrawerSection, setEditingDrawerSection] = useState<DrawerSection>(null)
  const [firstStepChannelIds, setFirstStepChannelIds] = useState<number[]>([])
  const [categoryConfiguration, setCategoryConfiguration] = useState<CategoryListingConfiguration | null>(null)
  const [ebayRequirements, setEbayRequirements] = useState(emptyEbayRequirements())
  const [loadingEbayRequirements, setLoadingEbayRequirements] = useState(false)
  const [loadingRefreshSpecifics, setLoadingRefreshSpecifics] = useState(false)
  const isDrawerOpenRef = useRef(isDrawerOpen)
  isDrawerOpenRef.current = isDrawerOpen
  const sideDrawerOpenedRef = useRef(false)
  const sideDrawerClosedBeforeListRef = useRef(false)
  const initialSideDrawerValuesRef = useRef<SideDrawerValues | null>(null)

  // analytics state
  const [didChangeDescription, setDidChangeDescription] = useState(false)
  const didChangeDescriptionRef = useRef(didChangeDescription)
  didChangeDescriptionRef.current = didChangeDescription
  const [didChangeTitle, setDidChangeTitle] = useState(false)
  const didChangeTitleRef = useRef(didChangeTitle)
  didChangeTitleRef.current = didChangeTitle
  const [didChangeBuyNowPrice, setDidChangeBuyNowPrice] = useState(false)
  const [didChangeShippingPrice, setDidChangeShippingPrice] = useState(false)
  const [didChangeShippingMethod, setDidChangeShippingMethod] = useState(false)

  // product fields
  const [title, setTitle] = useState(fetchedProduct?.title || '')
  const [canUseSeoTitle, setCanUseSeoTitle] = useState(false)
  const [description, setDescriptionNoAnalytics] = useState(fetchedProduct?.description || '')
  const setDescription = React.useCallback((newDescription: string) => {
    setDidChangeDescription(true)
    setDescriptionNoAnalytics(newDescription)
  }, [])

  const initialCategoryId = fetchedProduct?.category?.id || null
  const [categoryId, setCategoryId] = useState<number>(initialCategoryId)
  const categoryIdRef = useRef(categoryId)
  categoryIdRef.current = categoryId
  const categoryConfigRequestIdRef = useRef(0)
  const [images, setImages] = useState<any>(fetchedProduct?.images || [])
  const nonRemovedImages = React.useMemo(() => (images || []).filter(i => !i.removed), [images])
  const [tags, setTags] = useState<string[]>(fetchedProduct?.tags || [])
  const [additionalNote, setAdditionalNote] = useState('')
  const [sku, setSku] = useState(fetchedProduct?.sku || null)
  const [weight, setWeight] = useState(fetchedProduct?.weight > 0 ? fetchedProduct.weight.toString() : '')
  // We don't want to offer shipping weight, but some admins have template fields mapped to it.
  // So we need to let them change their template fields and do what they would expect.
  // This also enables orgs who set weight and shipping weight before posting time (if anyone does that).
  const [shippingWeight, setShippingWeight] = useState(
    parseFloat(fetchedProduct?.shippingWeight) > 0 ? fetchedProduct.shippingWeight : null
  )
  const [scaleName, scaleWeight, scaleConnected] = useScaleStreamer()
  const [length, setLength] = useState(fetchedProduct?.length > 0 ? fetchedProduct.length.toString() : '')
  const [width, setWidth] = useState(fetchedProduct?.width > 0 ? fetchedProduct.width.toString() : '')
  const [height, setHeight] = useState(fetchedProduct?.height > 0 ? fetchedProduct.height.toString() : '')
  const [shippingBoxId, setShippingBoxId] = useState<number>(fetchedProduct?.shippingBox?.id)
  const paddingChoices = props.current?.organization?.shippingSettings?.paddingChoices
  const initialBoxPadding = applyDefaultBoxPadding(
    { boxPadding: parseBoxPadding(fetchedProduct?.boxPadding) },
    paddingChoices
  ).boxPadding
  const [boxPadding, setBoxPadding] = useState<number | null>(initialBoxPadding)
  const [inventoryLocationId, setInventoryLocationId] = useState<number>(null) // not initialized b/c we omit this field if it's already set.
  const [supplierId, setSupplierId] = useState<number>(fetchedProduct?.supplier?.id ?? null)

  const [marketSettings, setMarketSettings] = useState<MarketSettings>(null)

  // fields defined by category configuration
  const [strategies, setStrategies] = useState<ListingStrategy[]>()

  // fields defined by strategy
  const [strategyId, setStrategyId] = useState<number>(null)
  // The channel id(s) on the 1st step.
  // These are the channels for which we allow the user to configure price.
  // const [firstStepChannelIds, setFirstStepChannelIds] = useState<unknown[]>(null) // LATER we'll want this

  // Has fields like price, bidIncrement, auctionStart, etc.
  // Includes fields that we show to the user.
  // Includes fields that we never show to the user, but they're easy to pull from the strategy configuration, to include in the request body when submitting.
  // This will still need a little massaging before we make an API call, for some duplicated fields like seller_private_description.
  //
  // A lot of these are typed as strings, though you would think it should be a number.
  // - Sometimes this helps with typing into numeric inputs
  // - Sometimes this stems from strategy configurations that give us the value as a string
  const [sgwForm, dispatch] = useReducer(sgwFormReducer, {})
  const [ebayForm, dispatchEbay] = useReducer(ebayFormReducer, { specifics: {}, productListingDetails: {} })
  const hasSgwChannel = hasShopgoodwillChannel(firstStepChannelIds)
  const hasEbayChannelSelected = hasEbayChannel(firstStepChannelIds)
  const calculatedShipping = hasSgwChannel && (!sgwForm.shippingPrice || parseFloat(sgwForm.shippingPrice) === 0.0) // if false, then flat-rate shipping

  const sgwPriceLabel = sgwForm.listingType === 'AUCTION' ? 'Starting bid' : 'Price'

  const [templateForm, setTemplateForm] = useState<TemplateFormItem[]>(null)
  const [templateLayout, setTemplateLayout] = useState<unknown[]>(null)
  const [templatePreviewTitle, setTemplatePreviewTitle] = useState<string>(null)
  const [templateBody, setTemplateBody] = useState<string>(null)
  const [templateAdminTitle, setTemplateAdminTitle] = useState(null)
  const [templateId, setTemplateId] = useState<number>(null)

  const recompileTemplateOutput = useCallback(
    (form: Record<string, unknown>[], templatePreviewTitle1: string, templateBody1: string, updateTitle = false) => {
      const newDescription = compiler(templateBody1, form, false, true)
      setDescriptionNoAnalytics(newDescription)
      if (updateTitle && templatePreviewTitle1 && !didChangeTitleRef.current) {
        setTitle(compiler(templatePreviewTitle1, form, true, true))
      }
    },
    []
  )

  const syncMappedProductFieldsFromTemplateForm = useCallback((form: TemplateFormItem[]) => {
    const attrs = getProductAttrsFromTemplateForm(form)

    if (attrs.length !== undefined) setLength(attrs.length.toString())
    if (attrs.width !== undefined) setWidth(attrs.width.toString())
    if (attrs.height !== undefined) setHeight(attrs.height.toString())
    if (attrs.weight !== undefined) setWeight(attrs.weight.toString())
    if (attrs.shippingWeight !== undefined) setShippingWeight(attrs.shippingWeight.toString())
  }, [])

  const setSEOTitle = () => {
    setTitle(t => compiler(templatePreviewTitle || t, templateForm, true, true))
  }

  // Forward sync: when template (builder) fields change, re-fill the eBay specifics/condition they map onto,
  // so the eBay form stays in sync as the user fills product attributes (mirrors legacy). Template wins on conflict.
  const applyEbayTemplateAutofill = useCallback(
    (form: TemplateFormItem[]) => {
      if (!hasEbayChannelSelected) return

      const autofilled = computeEbayAutofill(ebayRequirements, form)
      const hasSpecifics = !!autofilled.specifics && Object.keys(autofilled.specifics).length > 0

      if (!hasSpecifics && !autofilled.condition) return

      // Merge in the reducer against current state (not this callback's captured ebayForm.specifics).
      dispatchEbay({ type: 'applyAutofill', autofilled })
    },
    [hasEbayChannelSelected, ebayRequirements]
  )

  const onTemplateFormChange = useCallback(
    (index, value) => {
      setTemplateForm(t => {
        // Basic state management for the field contents
        const form = t.map((item: any, indexInner) => {
          if (indexInner === index) {
            if (item.productField === 'length') setLength(value)
            if (item.productField === 'width') setWidth(value)
            if (item.productField === 'height') setHeight(value)
            if (item.productField === 'weight') setWeight(value)
            if (item.productField === 'shippingWeight') setShippingWeight(value)

            return {
              ...item,
              props: {
                ...item.props,
                value
              }
            }
          }
          return item
        })

        // Every time the user changes builder template fields (product attributes), we want to recompile the template output.
        recompileTemplateOutput(form, templatePreviewTitle, templateBody, true)
        applyEbayTemplateAutofill(form)

        return form
      })
    },
    [recompileTemplateOutput, templatePreviewTitle, templateBody, applyEbayTemplateAutofill]
  )

  const onTemplateFormChangeCombobox = useCallback(
    (index, input: Record<string, unknown>) => {
      setTemplateForm(t => {
        const form = t.map((item: any, indexInner) => {
          if (indexInner === index) {
            return input
          }
          // Test this by creating a Builder Template with a combobox, then typing a custom value into it, then choosing another value.
          return item
        })

        // Every time the user changes builder template fields (product attributes), we want to recompile the template output.
        recompileTemplateOutput(form, templatePreviewTitle, templateBody, true)
        applyEbayTemplateAutofill(form)

        return form
      })
    },
    [recompileTemplateOutput, templatePreviewTitle, templateBody, applyEbayTemplateAutofill]
  )

  // the product fields which have a template field mapped to them. Like if "Couch width" is mapped to product.width
  let templateMappedFields = []
  templateForm?.forEach(item => {
    if (item.productField) {
      templateMappedFields.push(item.productField)
    }
  })
  templateMappedFields = uniq(templateMappedFields)

  const showDimensionsSection =
    !templateMappedFields.includes('length') ||
    !templateMappedFields.includes('width') ||
    !templateMappedFields.includes('height')

  // Prevents submission
  const missingRequiredFields = []
  let requiredFieldCount = 6
  if (safeIsEmpty(title)) missingRequiredFields.push('Title')
  if (safeIsEmpty(description)) missingRequiredFields.push('Description')
  if (safeIsEmpty(categoryId?.toString())) missingRequiredFields.push('Category')
  if (safeIsEmpty(strategyId?.toString())) missingRequiredFields.push('Listing Strategy')
  if (hasSgwChannel && safeIsEmpty(sgwForm.price)) missingRequiredFields.push(sgwPriceLabel)
  if (hasSgwChannel && safeIsEmpty(sgwForm.carrierId?.toString())) missingRequiredFields.push('Shipping Method')
  if (hasEbayChannelSelected && safeIsEmpty(ebayForm.startingPrice)) missingRequiredFields.push('eBay Price')
  if (hasEbayChannelSelected) {
    const unfilledEbaySpecifics = notFilledRequiredSpecifics(ebayForm, ebayRequirements)
    unfilledEbaySpecifics.forEach(field => {
      if (field.name === 'condition') {
        missingRequiredFields.push('eBay Condition')
      } else {
        missingRequiredFields.push(field.name)
      }
    })
  }
  if (props.current?.organization?.productFormRequiredFields?.includes('images')) {
    // Other places have pretty nuanced logic around whether images are required, see Product.should_validate_images?
    // But since express list can't be used when there are listings, we can naively follow what they configure in the admin settings.
    // As long as they don't do something silly (like omit images from being required AND turn on "list immediately"), they won't face any failed listings.

    if (safeIsEmpty(nonRemovedImages)) missingRequiredFields.push('Images')
    requiredFieldCount += 1
  }
  const weightRequired =
    (hasSgwChannel && calculatedShipping) ||
    props.current?.organization?.productFormRequiredFields?.includes('weight') ||
    (props.current?.organization?.productFormRequiredFields?.includes('shippingWeight') &&
      !templateMappedFields.includes('shippingWeight'))

  const shippingWeightRequired =
    // We usually don't even have a shipping weight field in express list.
    // But they might have a template field mapped to shipping weight.
    // This is debatably too nuanced/complicated, but let's try to honor various combinations of:
    // - template fields, mapped to Weight and/or Shipping Weight. Note, only fields mapped to weight (not shipping weight) will dedup the "Item Weight" product field.
    // - Required Posting Fields, either Weight and/or Shipping Weight
    // - calculated vs flat-rate shipping
    props.current?.organization?.productFormRequiredFields?.includes('shippingWeight') &&
    templateMappedFields.includes('shippingWeight')

  if (weightRequired && !templateMappedFields.includes('weight')) {
    if (weight === '') missingRequiredFields.push('Weight')

    requiredFieldCount += 1
  }
  // The shipping box only applies to Shopgoodwill (calculated shipping) and its picker is Shopgoodwill-gated,
  // so only require it when SGW is in play — otherwise an eBay-only user could never satisfy it.
  const boxRequired = hasSgwChannel && props.current?.organization?.productFormRequiredFields?.includes('shippingBoxId')
  if (boxRequired) {
    if (safeIsEmpty(shippingBoxId?.toString())) {
      missingRequiredFields.push('Shipping Box')
    }
    requiredFieldCount += 1
  }
  // We don't want people to assign locations at list time (versus using Link + scanning).
  // Yet a portion of customers do this and configure it as required in admin settings.
  const inventoryLocationRequired =
    props.current?.organization?.productFormRequiredFields?.includes('inventoryLocationId') &&
    !fetchedProduct?.inventoryLocation
  if (inventoryLocationRequired) {
    if (safeIsEmpty(inventoryLocationId?.toString())) {
      missingRequiredFields.push('Inventory Location')
    }

    requiredFieldCount += 1
  }
  const supplierRequired =
    props.current?.organization?.productFormRequiredFields?.includes('supplierId') && !fetchedProduct?.supplier
  if (supplierRequired) {
    if (safeIsEmpty(supplierId?.toString())) {
      missingRequiredFields.push('Supplier')
    }

    requiredFieldCount += 1
  }

  // Massage templateForm so that we mark fields as required, even if the builder template didn't configure it as required,
  // but our biz logic here DOES require it.
  // Namely, if they're using calculated shipping and thus weight is required, then template fields mapped to weight should be required.
  const massagedTemplateForm = templateForm?.map(item => {
    let overrideToRequired = false

    // Our biz logic currently never requires dimensions. But if we did, we would want this:
    // if (item.productField === 'length' || item.productField === 'width' || item.productField === 'height') {
    //   if (dimensionsRequired) overrideToRequired = true
    // }

    if (item.productField === 'weight') {
      if (weightRequired) {
        overrideToRequired = true
      }
    }

    if (item.productField === 'shippingWeight') {
      if (shippingWeightRequired) {
        overrideToRequired = true
      }
    }

    return {
      ...item,
      props: {
        ...item.props,
        required: overrideToRequired ? true : item.props.required,

        // if a weight-mapped template field becomes required due to our biz logic (calculated shipping), then force it to be shown
        hidden: overrideToRequired ? '' : item.props.hidden
      }
    }
  })

  let templateHasValue = false
  massagedTemplateForm?.forEach(item => {
    let empty
    const { value } = item.props
    const fieldHasValue = value !== undefined && value !== null && value !== '' && value !== false

    if (fieldHasValue) templateHasValue = true

    if (item.type === 'checkbox') {
      empty = !value // making a checkbox required doesn't make a lot of sense, but honor this if they configure it
    } else {
      empty = safeIsEmpty(value as string)
    }

    if (!item.props.required) {
      return
    }

    // Prevent hidden required fields from stopping the user from progressing.
    const hidden =
      item.props.hidden && // performance optimization, skip compiler and fromJS
      !safeIsEmpty(item.props.hidden) && // I'm not sure if item.props.hidden will be null or empty string.
      stringToBool(compiler(item.props.hidden, templateForm, false, false))
    if (hidden) {
      return
    }

    requiredFieldCount += 1
    if (empty) {
      missingRequiredFields.push(item.props.label)
    }
  })

  if (canUseSeoTitle !== templateHasValue) setCanUseSeoTitle(templateHasValue)

  const formLoading = loadingCategoryConfiguration || loadingStrategyConfiguration || loadingEbayRequirements
  const sgwFormReady = !hasSgwChannel || (sgwForm && Object.keys(sgwForm).length > 1)
  const ebayFormReady =
    !hasEbayChannelSelected || (ebayForm && !ebayRequirements.loading && !!ebayForm.categoryMarketCategoryId)
  const formLoaded = !formLoading && strategyId && sgwFormReady && ebayFormReady

  const getSideDrawerValues = useCallback(
    (): SideDrawerValues => ({
      listingStrategy: strategyId,
      buyNowPrice: sgwForm.buyItNowPrice ?? null,
      shippingPrice: sgwForm.shippingPrice ?? null,
      shippingMethod: sgwForm.carrierId ?? null,
      noCombineShipping: sgwForm.noCombineShipping ?? false,
      featuredItem: sgwForm.itemFeatured ?? false
    }),
    [strategyId, sgwForm]
  )

  // Capture the first loaded state after the drawer is opened. Keeping this
  // snapshot across subsequent opens lets the save event report all drawer
  // changes made during the listing session.
  useEffect(() => {
    if (isDrawerOpen && formLoaded && initialSideDrawerValuesRef.current === null) {
      initialSideDrawerValuesRef.current = getSideDrawerValues()
    }
  }, [isDrawerOpen, formLoaded, getSideDrawerValues])

  const getEditedSideDrawerFields = (): SideDrawerField[] => {
    const initialValues = initialSideDrawerValuesRef.current
    if (!initialValues) return []

    const currentValues = getSideDrawerValues()
    return SIDE_DRAWER_FIELDS.filter(field => !isEqual(initialValues[field], currentValues[field]))
  }

  const trackSideDrawerOpened = useCallback(() => {
    sideDrawerOpenedRef.current = true
    analytics.track('Express List Side Drawer Opened', {
      version: FORM_VERSION_STRING,
      organizationId,
      isNewProduct,
      ...(existingProductId != null ? { productId: existingProductId } : {})
    })
  }, [existingProductId, isNewProduct, organizationId])

  const openSideDrawer = useCallback(() => {
    trackSideDrawerOpened()
    setIsDrawerOpen(true)
  }, [trackSideDrawerOpened])

  const closeSideDrawer = useCallback(() => {
    sideDrawerClosedBeforeListRef.current = true
    setIsDrawerOpen(false)
  }, [])

  const [saveCompleted, setSaveCompleted] = useState(false)
  const [baselineSnapshot, setBaselineSnapshot] = useState<ReturnType<typeof getExpressListFormSnapshot> | null>(null)

  const captureCurrentSnapshot = useCallback(
    () =>
      getExpressListFormSnapshot({
        title,
        description,
        categoryId,
        strategyId,
        tags,
        images,
        weight,
        shippingWeight,
        length,
        width,
        height,
        shippingBoxId,
        boxPadding,
        inventoryLocationId,
        supplierId,
        additionalNote,
        sgwForm,
        ebayForm,
        templateForm
      }),
    [
      title,
      description,
      categoryId,
      strategyId,
      tags,
      images,
      weight,
      shippingWeight,
      length,
      width,
      height,
      shippingBoxId,
      boxPadding,
      inventoryLocationId,
      supplierId,
      additionalNote,
      sgwForm,
      ebayForm,
      templateForm
    ]
  )

  const currentSnapshot = captureCurrentSnapshot()

  const hasUnsavedChanges = !saveCompleted && baselineSnapshot !== null && !isEqual(baselineSnapshot, currentSnapshot)
  const configurationLoaded = formLoaded || strategies !== undefined
  const isInitializingResumedDraft = !!initialCategoryId && !!categoryId && (formLoading || !configurationLoaded)

  useEffect(() => {
    if (saveCompleted) {
      return
    }

    const shouldWaitForConfigurationLoad = !!categoryId
    if (shouldWaitForConfigurationLoad && (formLoading || !configurationLoaded)) {
      return
    }

    if (baselineSnapshot !== null) {
      return
    }

    setBaselineSnapshot(captureCurrentSnapshot())
  }, [saveCompleted, categoryId, formLoading, configurationLoaded, baselineSnapshot, captureCurrentSnapshot])

  // clear any fields that were configured by the strategy configuration
  const clearStrategyConfig = () => {
    setStrategyId(null)
    setFirstStepChannelIds([])
    setEbayRequirements(emptyEbayRequirements())
    dispatch({ type: 'clearStrategyForm' })
    dispatchEbay({ type: 'clearStrategyForm' })
  }

  const getPreservedListingOptions = (): PreservedListingOptions => ({
    noCombineShipping: sgwForm.noCombineShipping,
    itemFeatured: sgwForm.itemFeatured
  })

  // clear any fields that were configured by the category or strategy configuration
  const clearCategoryAndStrategyConfig = (preservedListingOptions?: PreservedListingOptions) => {
    setCategoryId(null)
    categoryIdRef.current = null
    setTemplateForm(null) // template was configured by the selected category, so clear it
    setTemplateLayout(null)
    setTemplateAdminTitle(null)
    setTemplateId(null)
    setFirstStepChannelIds([])
    setCategoryConfiguration(null)
    setEbayRequirements(emptyEbayRequirements())

    // strategy was generally auto-selected by the selected category, so clear it
    setStrategyId(null)
    dispatch({ type: 'clearForm', preservedListingOptions })
    dispatchEbay({ type: 'clearForm' })
  }

  // Redirect back to the starting page if they navigated straight to a draft product URL without state.
  // Functionally, we want users to use the kickoff page. This helps avoid listing a product twice.
  // Technically, it avoids having to fetch the product in 2 different ways.
  // New products use id "new" and can initialize from createEmptyExpressListProduct() when state is missing.
  useEffect(() => {
    if (!fetchedProduct) {
      props.history.replace('/express_list')
    }
  }, [fetchedProduct, props.history])

  const refreshEbayRequirements = useCallback(
    async (categoryMarketCategoryId: number, templateFormOverride?: TemplateFormItem[] | null) => {
      setLoadingEbayRequirements(true)
      await loadEbayRequirements({
        categoryMarketCategoryId,
        // On the initial category load the templateForm state hasn't committed yet, so callers pass the
        // freshly-built form explicitly. Fall back to state for later refreshes (e.g. strategy changes).
        templateForm: templateFormOverride ?? templateForm,
        onSuccess: ({ requirements, autofilled }) => {
          setEbayRequirements(requirements)
          const hasAutofill =
            (autofilled.specifics && Object.keys(autofilled.specifics).length > 0) || autofilled.condition
          if (hasAutofill) {
            // Merge in the reducer against current state (not this callback's captured ebayForm.specifics).
            dispatchEbay({ type: 'applyAutofill', autofilled })
          }
          setLoadingEbayRequirements(false)
        },
        onError: () => {
          setEbayRequirements(emptyEbayRequirements())
          setLoadingEbayRequirements(false)
        }
      })
    },
    [templateForm]
  )

  const onRefreshEbaySpecifics = useCallback(async () => {
    const cmcId = ebayForm.categoryMarketCategory?.id
    if (!cmcId) return

    setLoadingRefreshSpecifics(true)
    try {
      await Client.resetCategorySpecifics(cmcId)
      await refreshEbayRequirements(cmcId)
    } finally {
      setLoadingRefreshSpecifics(false)
    }
  }, [ebayForm.categoryMarketCategory?.id, refreshEbayRequirements])

  // Get the configuration for this listing strategy, such as starting price for each channel.
  // categoryConfigOverride is required when calling immediately after setCategoryConfiguration —
  // React state is still stale in that same tick.
  const fetchStrategyConfiguration = useCallback(
    async (
      strategyIdToFetch,
      categoryConfigOverride?: CategoryListingConfiguration | null,
      templateFormOverride?: TemplateFormItem[] | null
    ) => {
      const activeCategoryConfiguration = categoryConfigOverride ?? categoryConfiguration

      try {
        setLoadingStrategyConfiguration(true)
        const res = await Client.listingStrategyConfigurationLookup(strategyIdToFetch)
        const strategyConfiguration = res.data.configuration
        const channelIds = strategyConfiguration.map(c => c.marketId)
        setFirstStepChannelIds(channelIds)

        const categoryMappings = activeCategoryConfiguration?.category?.categoryMappings
        const sgwCmc = findActiveCmc(categoryMappings, SHOPGOODWILL_CHANNEL_ID)
        const ebayCmc = findActiveCmc(categoryMappings, EBAY_CHANNEL_ID)

        if (hasShopgoodwillChannel(channelIds) && !sgwCmc) {
          const strategyName: string = res.data.strategy.name
          showToast(
            `Strategy "${strategyName}" requires Shopgoodwill, but this category is not configured for Shopgoodwill. Please ask your admin to fix this.`,
            Intent.WARNING,
            false,
            8000
          )
          clearStrategyConfig()
          return
        }

        if (hasEbayChannel(channelIds) && !ebayCmc) {
          const strategyName: string = res.data.strategy.name
          showToast(
            `Strategy "${strategyName}" requires eBay, but this category is not configured for eBay. Please ask your admin to fix this.`,
            Intent.WARNING,
            false,
            8000
          )
          clearStrategyConfig()
          return
        }

        dispatch({ type: 'strategyConfigLoaded', resData: res.data })
        dispatchEbay({
          type: 'strategyConfigLoaded',
          resData: res.data,
          marketSettings: activeCategoryConfiguration?.marketSettings,
          category: activeCategoryConfiguration?.category,
          carrierId: sgwForm.carrierId
        })

        if (hasEbayChannel(channelIds) && ebayCmc?.id) {
          await refreshEbayRequirements(ebayCmc.id, templateFormOverride)
        } else {
          setEbayRequirements(emptyEbayRequirements())
        }
      } catch (e) {
        Sentry.captureException(e)
        clearStrategyConfig()
        showFailure('Something went wrong, please choose a strategy again', 8000)
      } finally {
        setLoadingStrategyConfiguration(false)
      }
    },
    [categoryConfiguration, refreshEbayRequirements, sgwForm.carrierId]
  )

  // Get the configuration for this category, such as the list of strategies that can be used.
  const loadCategoryConfiguration = useCallback(
    async (
      categoryIdToFetch,
      options?: { previousTemplateForm?: TemplateFormItem[]; preservedListingOptions?: PreservedListingOptions }
    ) => {
      categoryConfigRequestIdRef.current += 1
      const requestId = categoryConfigRequestIdRef.current
      const isLatestCategoryConfigRequest = () => requestId === categoryConfigRequestIdRef.current

      const params = {
        marketIds: expressListMarketIds(uniqueConnectedMarkets)
      }

      try {
        setLoadingCategoryConfiguration(true)
        const res = await Client.categoryListingConfiguration(categoryIdToFetch, params)

        if (!isLatestCategoryConfigRequest()) {
          return
        }

        const { templates, listingStrategies } = res.data

        setStrategies(listingStrategies)
        setMarketSettings(res.data.marketSettings)
        setCategoryConfiguration(res.data)

        const sgwCmcId = findActiveCmc(res.data.category?.categoryMappings, SHOPGOODWILL_CHANNEL_ID)?.id
        const ebayCmc = findActiveCmc(res.data.category?.categoryMappings, EBAY_CHANNEL_ID)

        if (!sgwCmcId && !ebayCmc) {
          const message = `Category "${res.data.category.pathCache}" isn't configured for Express List marketplaces. Please ask your admin to fix this.`

          if (categoryIdToFetch === categoryIdRef.current || categoryIdRef.current == null) {
            clearCategoryAndStrategyConfig()
          }
          showToast(message, Intent.WARNING, false, 8000)

          return
        }

        let freshTemplateForm: TemplateFormItem[] | null = null
        if (templates.length > 0) {
          const template = initializeTemplateState(fetchedProduct, templates, options?.previousTemplateForm)
          freshTemplateForm = template.form
          setTemplateForm(template.form)
          setTemplateLayout(template.layout)
          setTemplatePreviewTitle(template.previewTitle)
          setTemplateBody(template.body)
          setTemplateAdminTitle(template.title)
          setTemplateId(template.id)
          syncMappedProductFieldsFromTemplateForm(template.form)
          // Recompile unless the user manually edited the description (e.g. static template insertions).
          if (!didChangeDescriptionRef.current) {
            recompileTemplateOutput(template.form, template.previewTitle, template.body)
          }
        }

        if (!isLatestCategoryConfigRequest()) {
          return
        }

        if (categoryIdToFetch === categoryIdRef.current) {
          if (sgwCmcId) {
            dispatch({
              type: 'categoryConfigLoaded',
              resData: res.data,
              cmcId: sgwCmcId,
              preservedListingOptions: options?.preservedListingOptions
            })
          }

          dispatchEbay({
            type: 'categoryConfigLoaded',
            resData: res.data,
            carrierId: res.data.defaultCarrierId
          })

          if (listingStrategies.length > 0) {
            const newStrategyId = getPreferredStrategy(listingStrategies)?.id
            setStrategyId(newStrategyId)

            fetchStrategyConfiguration(newStrategyId, res.data, freshTemplateForm)
          }
        }
      } catch (e) {
        Sentry.captureException(e)

        // A stale request should not wipe a category the user picked afterward.
        if (categoryIdToFetch === categoryIdRef.current || categoryIdRef.current == null) {
          // Clears out any fields, possibly from another category, which might be invalid.
          // Also forces them to choose the category again, to fire off API call again
          clearCategoryAndStrategyConfig()

          // The API response (if this is an API failure) won't have a useful message
          showFailure('Something went wrong, please choose a category again', 8000)
        }
      } finally {
        if (isLatestCategoryConfigRequest()) {
          setLoadingCategoryConfiguration(false)
        }
      }
    },
    [
      fetchStrategyConfiguration,
      fetchedProduct,
      recompileTemplateOutput,
      syncMappedProductFieldsFromTemplateForm,
      uniqueConnectedMarkets
    ]
  )

  const onChangeStrategy = async newStrategyId => {
    if (newStrategyId) {
      // Don't leave stale form values from a previously selected strategy.
      // Particularly important for fields like "shipping price", where null means something significant (calculated shipping).
      clearStrategyConfig()

      setStrategyId(newStrategyId)
      await fetchStrategyConfiguration(newStrategyId)
    } else {
      // they typed backspace to clear the value. We can't prevent this behavior.
      clearStrategyConfig()
    }
  }

  // Handle case where we display this page, and the category was already set on the product.
  // Note, react fast refresh code changes will fire useEffect calls more frequently than regular dev / production.
  useEffect(() => {
    if (initialCategoryId) {
      loadCategoryConfiguration(initialCategoryId)
    }
    // Intentionally omit loadCategoryConfiguration: only auto-load the draft category on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategoryId])

  // Automatically generate a sku. Wait for a required supplier so prefixes match and we don't burn a sequence.
  useEffect(() => {
    let cancelled = false

    if (!skuFromDraft && fetchedProduct && !(supplierRequired && !supplierId)) {
      const maybeGenerateSku = async () => {
        try {
          const newSku = (await Client.newSku(supplierId ?? fetchedProduct.supplier?.id)).data.sku
          if (!cancelled) setSku(newSku)
        } catch (e) {
          Sentry.captureException(e)
          if (!cancelled) showToast('Could not generate SKU, please try again', Intent.WARNING, false)
        }
      }

      maybeGenerateSku()
    }

    return () => {
      cancelled = true
    }
  }, [fetchedProduct, skuFromDraft, supplierId, supplierRequired])

  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [submitFieldErrors, setSubmitFieldErrors] = useState(null)
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null)
  const [shouldPrint, setShouldPrint] = useRememberedState(AUTO_PRINT_STORAGE_KEY, false, false)

  const elapsedSeconds = useTimerSeconds()
  const { listerConnectEnabled } = props.current.organization.settings

  const submit = async () => {
    setLoadingSubmit(true)
    try {
      // Serialize the product + listing attributes

      const { listingType, price, buyItNowPrice, carrierId, ...otherSgwAttributes } = sgwForm
      let shopgoodwillListingAttributes: Record<string, any> | undefined

      const resolvedTitle =
        templatePreviewTitle && templateForm && !didChangeTitleRef.current
          ? compiler(templatePreviewTitle, templateForm, true, true)
          : title

      if (hasSgwChannel) {
        shopgoodwillListingAttributes = { ...otherSgwAttributes }

        shopgoodwillListingAttributes.title = resolvedTitle
        shopgoodwillListingAttributes.description = description

        if (marketSettings?.shopgoodwill?.sendSkuAsPrivateDescription) {
          shopgoodwillListingAttributes.sellerPrivateDescription = sku
        }

        if (listingType === 'AUCTION') {
          shopgoodwillListingAttributes.startingBid = price

          if (buyItNowPrice) {
            shopgoodwillListingAttributes.buyNowPrice = buyItNowPrice
          }
        }
        if (listingType === 'FIXED_PRICE') {
          shopgoodwillListingAttributes.startingBid = price
          shopgoodwillListingAttributes.buyNowPrice = price
        }
        if (shopgoodwillListingAttributes.shippingPrice === null) {
          delete shopgoodwillListingAttributes.shippingPrice
        }
      }

      let ebayListingAttributes: Record<string, any> | undefined
      if (hasEbayChannelSelected) {
        const requireFieldSpecifics = notFilledRequiredSpecifics(ebayForm, ebayRequirements)
        ebayListingAttributes = {
          ...ebayForm,
          title: resolvedTitle,
          description,
          specifics: Object.keys(ebayForm.specifics).map(k => ({ name: k, value: ebayForm.specifics[k] })),
          required_specifics: requireFieldSpecifics,
          categoryMarketCategoryId: ebayForm.categoryMarketCategory?.id,
          quantity: ebayForm.listingType === 'Chinese' ? 1 : ebayForm.quantity
        }
        delete ebayListingAttributes.categoryMarketCategory
        delete ebayListingAttributes.oldCategoryConfig
      }

      let templateFieldValuesAsJson = null
      if (templateForm) {
        // For example, [
        //   { "name": "Brand", "value": "Patagonia", "type": "input" },
        //   { "name": "Size", "value": "XL", "type": "select" }
        // ]
        const templateFieldValues = templateForm.map(e => {
          let { value } = e.props
          if (e.type === 'checkbox') {
            value = !!value // make sure unchecked checkboxes are serialized as false
          }

          return {
            name: e.props.label,
            value,
            type: e.type
          }
        })

        // templateFieldValues has the crazy keys we want (with spaces, symbols, etc). But axios is snake-casing them.
        // Ideally we would disable that just for this one key, using the "preservedKeys" config option.
        // But preservedKeys isn't recursive, so just send up a JSON string.
        templateFieldValuesAsJson = JSON.stringify(templateFieldValues)
      }

      const imagesAttributes = images
        .filter(i => !i.loading)
        .sort((a, b) => (a.removed ? 1 : 0) - (b.removed ? 1 : 0)) // put removed images at the end, so remaining images don't skip ranks
        .map((i, rank) => {
          const { removed, ...endpointAttributes } = i // strip non-endpoint attributes (nice to have)

          return { ...endpointAttributes, rank, rotation: i.rotation % 360, customDestroyFlag: i.removed }
        })

      // This helper function from classic list generates several attributes, like LWH and tags.
      // We're only pulling tags here, because we want to manage the other state in more real-time,
      // so that the other state can be used for real-time validation (like drawing the progress bar).
      const tagsFromTemplate = templateForm && getProductAttrsFromTemplateForm(templateForm).tags
      let allTags = tags
      if (tagsFromTemplate) {
        allTags = allTags.concat(tagsFromTemplate)
      }

      const resolvedBoxPadding = applyDefaultBoxPaddingIfDimensionsPresent(
        { boxPadding, length, width, height },
        paddingChoices
      ).boxPadding

      const request: Record<string, unknown> = {
        ...(existingProductId != null ? { id } : {}),
        title: resolvedTitle,
        description,
        categoryId,
        listingStrategyId: strategyId,
        sku,
        imagesAttributes,
        tags: allTags,
        additionalNote,
        length,
        width,
        height,
        weight: weight === '' ? '0.0' : weight, // this jives with how serialization / defaults work on the backend
        // We don't need to allow users to enter an item weight PLUS a higher shipping weight
        // (for the case of having 2 lbs worth of packing peanuts + corrugate).
        // But, honor it if they already set it on the product. Or if they mapped a builder template field to shipping weight.
        shippingWeight: shippingWeight || weight,
        shippingBoxId,
        boxPadding: resolvedBoxPadding,
        formVersion: FORM_VERSION_INTEGER, // Used for GMV analytics.
        ...(shopgoodwillListingAttributes ? { shopgoodwillListingAttributes } : {}),
        ...(ebayListingAttributes ? { ebayListingAttributes } : {}),
        templateFieldValuesAsJson
      }
      if (hasSgwChannel && carrierId) {
        request.carrierId = carrierId
      }
      if (inventoryLocationId) {
        // We only display this form field if it's required and missing.
        // For other cases, omit this field to use existing value. Also sidesteps bug where someone reshelfs while this form was being filled out.
        request.inventoryLocationId = inventoryLocationId
      }
      if (supplierId) {
        // We only display this form field if it's required and missing.
        // For other cases, omit this field to use existing value on the draft product.
        request.supplierId = supplierId
      }
      setSubmitFieldErrors(null)
      setSubmitErrorMessage(null)

      const res = await Client.createProductV5(request)

      const success = res.data.product.id && res.data.product.title
      if (success) {
        showToast(`${sku || 'Product'} was listed`, Intent.SUCCESS)

        const reactRouterState: Record<string, unknown> = {
          requiredFieldCount,
          elapsedSeconds
        }

        let autoPrinted = false
        if (shouldPrint && listerConnectEnabled) {
          autoPrinted = true
          const printResult = await tryPrintExpressListProductLabel({
            productId: res.data.product.id,
            sku: typeof sku === 'string' ? sku : null
          })
          if (!printResult.ok) {
            reactRouterState.printFailed = true
            reactRouterState.printProductId = res.data.product.id
            if (printResult.skuTooLong) {
              reactRouterState.printSkuTooLong = true
            }
            if (typeof sku === 'string') {
              reactRouterState.printSku = sku
            }
          }
        }

        // Replace, because clicking back and seeing the same form again would be bad.
        // The product was already saved, and bugs will happen if they submit a second time.
        flushSync(() => {
          setSaveCompleted(true)
        })
        props.history.replace('/express_list', reactRouterState)

        analytics.track('Product Created Express List', {
          version: FORM_VERSION_STRING,
          productId: res.data.product.id,
          isNewProduct,
          organizationId: props.current.organization.id,
          timeToListDuration: elapsedSeconds,
          autoPrinted,
          didChangeDescription, // It's ambitious, but maybe users won't have to change the description
          didChangeBuyNowPrice, // fields in the drawer. Hopefully users aren't changing these.
          didChangeShippingPrice,
          didChangeShippingMethod,
          sideDrawerOpened: sideDrawerOpenedRef.current,
          sideDrawerClosedBeforeList: sideDrawerClosedBeforeListRef.current,
          listedWithSideDrawerOpen: isDrawerOpenRef.current,
          sideDrawerEditedFields: getEditedSideDrawerFields()
        })
      }
    } catch (e) {
      if (e.response?.data?.errors && Object.keys(e.response.data.errors).length > 0) {
        setSubmitFieldErrors(e.response.data.errors)

        // Show errors in the callout, but only if they're not junk AND not displayed elsewhere

        // Unpack the fields that we:
        // - explicitly parse and show to the user below a form field
        // - would never show to the user (because it's noise / a duplicate / etc)
        const {
          shopgoodwillListingStartingBid, // we explicitly parse
          shopgoodwillListingDescription, // duplicate of product description
          shopgoodwillListing, // noise
          ...otherErrorFields
        } = e.response.data.errors

        if (Object.keys(otherErrorFields).length === 0) {
          // There's nothing that can only be shown in the callout
          setSubmitErrorMessage('Please fix the errors below and try again.')
        } else if (otherErrorFields.variantsPrice && otherErrorFields.variantsPrice[0] === 'is required') {
          // Users shouldn't be trying to list draft products with variants. But I did, and the default message is confusing.
          setSubmitErrorMessage('Products with variants are unsupported in Express List.')
        } else {
          // Some error fields couldn't be displayed in the form.
          // So to be safe, show a decent message to the user
          setSubmitErrorMessage(
            `Please fix these errors and try again: ${getErrorString(
              { response: { data: { errors: otherErrorFields } } },
              Sentry.captureException
            )}`
          )
        }
      } else {
        // In this situation, they're probably getting a 500, so getErrorString is probably "We're sorry, something went wrong"
        setSubmitErrorMessage(`Error: ${getErrorString(e, Sentry.captureException)}`)
      }
    } finally {
      setLoadingSubmit(false)
    }
  }

  const saveButtonEnabled = formLoaded && missingRequiredFields.length === 0
  const saveButtonText = shouldPrint ? 'Save, list & print' : 'Save & list'
  const saveButtonIcon = shouldPrint ? IconNames.PRINT : IconNames.DOCUMENT
  const saveTooltipContent = (
    <div className="tooltip-content" data-testid="save-button-tooltip">
      <span>Please fill out:</span>
      <ul>
        {missingRequiredFields.map(s => (
          <li key={s}>{s}</li>
        ))}
      </ul>
    </div>
  )

  const onChangeShippingBox = React.useCallback((e: { name: string; value: number }) => {
    setShippingBoxId(e.value)
  }, [])

  const onChangeImages = React.useCallback((newImages, cb) => {
    // newImages will only have the images we passed into Uploader.
    // We still need to keep all the images (including removed ones) in our state
    setImages(s => newImages.concat((s || []).filter(i => i.removed)))
    if (cb) {
      cb()
    }
  }, [])

  if (!fetchedProduct) {
    // Users shouldn't see this.
    // Avoids memory leaks from LoadableSelect running.
    return (
      <div>
        <p>Returning to starting page...</p>
      </div>
    )
  }

  const showTooltipsDuringTesting = config.TEST ? { isOpen: true } : {}

  return (
    <div className="express-list-page">
      <Prompt when={hasUnsavedChanges} message="You have unsaved changes, are you sure you want to leave?" />
      <div className="header sticky">
        <div className="header-content">
          <div className="left-header-things">
            <Link to="/express_list">
              <Button text="Back" icon={IconNames.CHEVRON_LEFT} intent={Intent.PRIMARY} minimal />
            </Link>
            <h1>Express List</h1>

            <ProgressBar
              formLoading={formLoading}
              requiredFieldCount={requiredFieldCount}
              missingRequiredFields={missingRequiredFields}
              topRightElement={<Timer elapsedSeconds={elapsedSeconds} />}
            />
          </div>

          {listerConnectEnabled ? (
            <ButtonGroup className="express-list-save-button-group">
              <Tooltip
                disabled={saveButtonEnabled}
                content={saveTooltipContent}
                position="bottom"
                {...showTooltipsDuringTesting}
              >
                <Button
                  large
                  intent={Intent.PRIMARY}
                  icon={saveButtonIcon}
                  onClick={submit}
                  loading={loadingSubmit}
                  disabled={!saveButtonEnabled}
                >
                  {saveButtonText}
                </Button>
              </Tooltip>
              <Popover
                position="bottom-right"
                disabled={!saveButtonEnabled}
                interactionKind="click"
                content={
                  <Menu>
                    <MenuItem
                      icon={IconNames.DOCUMENT}
                      text="Save & list"
                      disabled={!shouldPrint}
                      onClick={() => setShouldPrint(false)}
                    />
                    <MenuItem
                      icon={IconNames.PRINT}
                      text="Save, list & print"
                      disabled={shouldPrint}
                      onClick={() => setShouldPrint(true)}
                    />
                  </Menu>
                }
              >
                <Button
                  large
                  intent={Intent.PRIMARY}
                  rightIcon={IconNames.CARET_DOWN}
                  disabled={!saveButtonEnabled}
                  loading={loadingSubmit}
                  data-testid="express-list-save-menu-toggle"
                />
              </Popover>
            </ButtonGroup>
          ) : (
            <Tooltip
              disabled={saveButtonEnabled}
              content={saveTooltipContent}
              position="bottom"
              {...showTooltipsDuringTesting}
            >
              <Button intent={Intent.PRIMARY} onClick={submit} loading={loadingSubmit} disabled={!saveButtonEnabled}>
                Save and List
              </Button>
            </Tooltip>
          )}
        </div>
        <div className="dummy-collapsed-drawer-spacer" />
      </div>

      <div className="content-and-collapsed-drawer-container">
        <div className="main-content">
          {submitErrorMessage && (
            <div className="callout-container">
              <div className="callout-content">
                <Callout intent={Intent.DANGER}>{submitErrorMessage}</Callout>
              </div>
            </div>
          )}

          <div className="column-container">
            <div className="column">
              <Card elevation={Elevation.ONE} data-testid="card-product">
                <h1>PRODUCT</h1>
                <FormGroup
                  label="Title"
                  labelFor="title-input"
                  labelInfo={safeIsEmpty(title) && <Icon icon="warning-sign" />}
                >
                  <div className="title-input-container">
                    {/* Doesn't show the max length yet, but counting chars for them eliminates 80% of the friction around title length */}
                    <Tag className="char-count-tag" minimal>
                      {title ? title.length : 0}
                    </Tag>

                    {/* Hide the button if there are no product attributes.
                    Show it before the category/template/etc is loaded to minimize flickering. People usually use builder templates. */}
                    {(!formLoaded || templateForm) && (
                      <Tag className="use-seo-title" minimal>
                        <Tooltip
                          content={
                            <div style={{ maxWidth: 275 }}>
                              {canUseSeoTitle
                                ? 'Auto-populate a Search Engine Optimized title from product attribute field values'
                                : 'Please fill out Product Attributes first'}
                            </div>
                          }
                        >
                          <Button
                            minimal
                            className={canUseSeoTitle ? 'use-seo-title-button' : 'use-seo-title-button-disabled'}
                            disabled={!canUseSeoTitle}
                            icon={<Icon size={14} icon="clean" />}
                            onClick={setSEOTitle}
                          >
                            Generate SEO Title
                          </Button>
                        </Tooltip>
                      </Tag>
                    )}

                    <TextArea
                      id="title-input"
                      growVertically
                      value={title}
                      disabled={isInitializingResumedDraft}
                      onChange={event => {
                        setDidChangeTitle(true)
                        setTitle(event.target.value)
                      }}
                    />
                  </div>
                </FormGroup>

                <FormGroup label="Category" labelInfo={!categoryId && <Icon icon="warning-sign" />}>
                  <div data-testid="category-formgroup">
                    <ExpressListCategorySelect
                      load={() => {
                        return Client.categoryIndex(expressListMarketIds(uniqueConnectedMarkets))
                      }}
                      value={categoryId}
                      disabled={isInitializingResumedDraft}
                      onChange={v => {
                        const newCategoryId = v
                        const previousTemplateForm = templateForm
                        const preservedListingOptions = getPreservedListingOptions()
                        clearCategoryAndStrategyConfig(preservedListingOptions)
                        setCategoryId(newCategoryId)
                        categoryIdRef.current = newCategoryId
                        if (newCategoryId) {
                          loadCategoryConfiguration(newCategoryId, { previousTemplateForm, preservedListingOptions })
                        }
                      }}
                      autoload
                    />
                  </div>
                </FormGroup>

                <StrategyPicker
                  maybe
                  label="Listing Strategy"
                  labelInfo={!strategyId && <Icon icon="warning-sign" />}
                  strategies={strategies}
                  strategyId={strategyId}
                  setStrategyId={onChangeStrategy}
                  disabled={isInitializingResumedDraft}
                />

                {supplierRequired && (
                  <FormGroup
                    label="Supplier"
                    labelInfo={safeIsEmpty(supplierId?.toString()) && <Icon icon="warning-sign" />}
                  >
                    <div data-testid="supplier-form-group">
                      <LoadableSelect
                        load={() => Client.suppliersIndex({ listingStrategyId: strategyId })}
                        process={supplierSelectOptions}
                        onChange={setSupplierId}
                        value={supplierId}
                        autoload
                      />
                    </div>
                  </FormGroup>
                )}

                {inventoryLocationRequired && (
                  <FormGroup
                    label="Inventory Location"
                    labelInfo={safeIsEmpty(inventoryLocationId?.toString()) && <Icon icon="warning-sign" />}
                  >
                    <div data-testid="inventory-location-form-group">
                      <IndexedInventoryLocationSelect
                        autoload
                        value={inventoryLocationId}
                        onChange={setInventoryLocationId}
                        skipIndex
                      />
                    </div>
                  </FormGroup>
                )}
              </Card>

              {formLoading && (
                <Card elevation={Elevation.ONE} className="bp5-skeleton">
                  <div data-testid="configuration-loading-spinner">
                    {/* Placeholder makes the loading skeleton have the same height as final content. */}
                    <h1>loading placeholder</h1>
                    <FormGroup label="loading placeholder">
                      <ExpressListPriceInput value="0.00" onValueChange={_x => {}} />
                    </FormGroup>
                  </div>
                </Card>
              )}

              {formLoaded && hasSgwChannel && (
                <div data-testid="card-sgw">
                  <Card elevation={Elevation.ONE} className={formLoading && 'bp5-skeleton'}>
                    <h1>{marketChannelLabel(SHOPGOODWILL_CHANNEL_ID)}</h1>

                    <FormGroup
                      label={sgwPriceLabel}
                      labelFor="sgw-price-input"
                      // Note, this red helperText should rarely appear. We disable submission if this is blank
                      // Test by triggering minimum_reserve_price (set reserve_price <= starting_bid)
                      helperText={renderErrorButPreferRequired(submitFieldErrors?.shopgoodwillListingStartingBid)}
                      labelInfo={safeIsEmpty(sgwForm.price) && <Icon icon="warning-sign" />}
                    >
                      <ExpressListPriceInput
                        // We considered making this icon flex (the icon inside the input), based on whether it was filled out or missing.
                        // However, for consistency with other fields, we're going to put the warning-sign on the text label.
                        leftIcon="dollar"
                        fill
                        // Note, this danger intent should rarely appear. We disable submission if this is blank
                        intent={submitFieldErrors?.shopgoodwillListingStartingBid ? Intent.DANGER : null}
                        id="sgw-price-input"
                        value={sgwForm.price}
                        onValueChange={(valueAsString: string) => {
                          dispatch({
                            type: 'onValueChange',
                            newValues: { price: valueAsString }
                          })
                        }}
                      />
                    </FormGroup>
                  </Card>
                </div>
              )}

              {formLoaded && hasEbayChannelSelected && (
                <Card elevation={Elevation.ONE} data-testid="card-ebay-wrapper">
                  <ExpressListEbayCard
                    ebayForm={ebayForm}
                    ebayRequirements={ebayRequirements}
                    submitFieldErrors={submitFieldErrors}
                    loadingRefreshSpecifics={loadingRefreshSpecifics}
                    onPriceChange={(valueAsString: string) => {
                      dispatchEbay({
                        type: 'onValueChange',
                        newValues: { startingPrice: valueAsString }
                      })
                    }}
                    onFormChange={({ name, value }) => {
                      dispatchEbay({
                        type: 'onValueChange',
                        newValues: { [name]: value }
                      })
                    }}
                    onSpecificChange={(value, specificIndex) => {
                      const specific = ebayRequirements.specifics[specificIndex]
                      dispatchEbay({
                        type: 'onSpecificChange',
                        name: specific.name,
                        value
                      })
                    }}
                    onProductDetailChange={({ name, value }) => {
                      dispatchEbay({
                        type: 'onProductDetailChange',
                        name,
                        value
                      })
                    }}
                    onRefreshSpecifics={onRefreshEbaySpecifics}
                  />
                </Card>
              )}

              {(formLoaded || !strategyId) && (
                <Card elevation={Elevation.ONE} data-testid="card-weight-measurements">
                  <h1>WEIGHT AND MEASUREMENTS</h1>
                  {/*
                  Note, weight and dimensions are always shown, even if they aren't required (like for flat-rate shipping).
                  It's moderately likely that buyers will want this info regardless.
                  It's conceivable that later steps of the strategy might change to calculated shipping.
                  */}

                  {!templateMappedFields.includes('weight') && (
                    <WeightInput
                      label="Item Weight"
                      labelFor="item-weight"
                      labelInfo={weightRequired && safeIsEmpty(weight) && <Icon icon="warning-sign" />}
                      value={weight}
                      onValueChange={(_valueAsNumber, valueAsString) => {
                        setWeight(valueAsString)
                      }}
                      min={0}
                      minorStepSize={0.001}
                      selectAllOnFocus
                      fill
                      buttonPosition="none"
                      scaleName={scaleName}
                      scaleWeight={scaleWeight}
                      scaleConnected={scaleConnected}
                    />
                  )}

                  {showDimensionsSection && (
                    <FormGroup label="Dimensions">
                      <div className="dimensions">
                        {!templateMappedFields.includes('length') && (
                          <DimensionInput
                            value={length}
                            onValueChange={(_valueAsNumber, valueAsString) => {
                              setLength(valueAsString)
                            }}
                            rightLabel="L"
                          />
                        )}

                        {!templateMappedFields.includes('width') && (
                          <DimensionInput
                            value={width}
                            onValueChange={(_valueAsNumber, valueAsString) => {
                              setWidth(valueAsString)
                            }}
                            rightLabel="W"
                          />
                        )}

                        {!templateMappedFields.includes('height') && (
                          <DimensionInput
                            value={height}
                            onValueChange={(_valueAsNumber, valueAsString) => {
                              setHeight(valueAsString)
                            }}
                            rightLabel="H"
                          />
                        )}
                      </div>
                    </FormGroup>
                  )}

                  <FormGroup label="Box Padding" className="nuke-top-margin-loadable-select">
                    <div data-testid="box-padding-formgroup">
                      <LoadableSelect
                        load={() => props.current.organization.shippingSettings.paddingChoices}
                        raw
                        process={shippingBoxPaddingChoices =>
                          shippingBoxPaddingChoices.map(choice => ({
                            value: parseFloat(choice.value),
                            label: choice.name
                          }))
                        }
                        onChange={setBoxPadding}
                        value={boxPadding}
                        autoload
                      />
                    </div>
                  </FormGroup>

                  {!calculatedShipping && hasSgwChannel && (
                    <Callout
                      intent={Intent.PRIMARY}
                      className="flat-rate-shipping-callout"
                      icon={<SvgIcon path="/assets/icons/info-sign-inverted.svg" />}
                    >
                      <strong>Shipping Price Override</strong>
                      <br />
                      <p>
                        Calculated shipping not used, the shipping price is set to ${sgwForm.shippingPrice}. Use the
                        info panel to make changes.
                      </p>
                    </Callout>
                  )}
                </Card>
              )}

              {hasSgwChannel && (
                <Card elevation={Elevation.ONE}>
                  <h1>SHIPPING DETAILS</h1>
                  <div data-testid="shipping-box-formgroup">
                    <ShippingBoxSelect
                      onChange={onChangeShippingBox}
                      value={shippingBoxId}
                      length={length}
                      width={width}
                      height={height}
                      padding={boxPadding}
                      autoload
                      autoCalculate
                      labelInfo={boxRequired && safeIsEmpty(shippingBoxId?.toString()) && <Icon icon="warning-sign" />}
                    />
                  </div>
                </Card>
              )}
            </div>
            <div className="column">
              <Card
                elevation={Elevation.ONE}
                className="card-descr-or-attributes"
                data-testid="card-descr-or-attributes"
              >
                <ScaleContext.Provider value={{ scaleWeight, scaleName, scaleConnected }}>
                  <DescriptionOrAttributes
                    description={description}
                    setDescription={setDescription}
                    templateForm={massagedTemplateForm}
                    templateLayout={templateLayout}
                    onTemplateFormChange={onTemplateFormChange}
                    onTemplateFormChangeCombobox={onTemplateFormChangeCombobox}
                    loaded={formLoaded}
                    categorySelected={!!categoryId}
                    strategySelected={!!strategyId}
                    templateId={templateId}
                  />
                </ScaleContext.Provider>
              </Card>
              {config.DEV && (
                // Somehow customers can do template maintenance without being told which template they saw. But I can't.
                <p>Template: {templateAdminTitle}</p>
              )}
            </div>

            <div className="column images-column">
              <Card elevation={Elevation.ONE} data-testid="images-card">
                <h1>IMAGES</h1>

                <div className="warning-icon-container bp5-text-muted">
                  {missingRequiredFields.includes('Images') && <Icon icon="warning-sign" />}
                </div>

                <Uploader maxImages={config.MAX_IMAGES} images={nonRemovedImages} onChange={onChangeImages} />

                <br />
                <br />
              </Card>

              <Card elevation={Elevation.ONE} data-testid="tags-card">
                <h1>TAGS</h1>

                <TagSelector value={tags} onChange={setTags} />
              </Card>

              <Card elevation={Elevation.ONE} className="notes-card" data-testid="notes-card">
                <h1>NOTES</h1>

                {existingProductId != null && (
                  <NotesRenderer allowNewOrEditNotes={false} resource="Product" resourceId={existingProductId} />
                )}

                <FormGroup label="Additional Note">
                  <TextArea
                    fill
                    placeholder="Enter any internal information about this product (e.g. handling instructions for warehouse). This will not be shown on public listings."
                    value={additionalNote}
                    onChange={e => setAdditionalNote(e.target.value)}
                  />
                </FormGroup>
              </Card>
            </div>

            <Drawer
              isOpen={isDrawerOpen}
              onClose={() => {
                closeSideDrawer()
                setEditingDrawerSection(null)
              }}
              size="500px"
            >
              <div className={CX('bp5-dark', 'express-list-drawer')} data-testid="express-list-drawer">
                <ExpressListDrawer
                  formLoading={formLoading}
                  sku={sku}
                  strategies={strategies}
                  strategyId={strategyId}
                  onChangeStrategy={onChangeStrategy}
                  firstStepChannelIds={firstStepChannelIds}
                  sgwForm={sgwForm}
                  ebayForm={ebayForm}
                  calculatedShipping={calculatedShipping}
                  editingSection={editingDrawerSection}
                  onToggleSection={setEditingDrawerSection}
                  strategyDisabled={isInitializingResumedDraft}
                  onSgwValueChange={newValues => {
                    dispatch({ type: 'onValueChange', newValues })
                  }}
                  onEbayValueChange={newValues => {
                    dispatchEbay({ type: 'onValueChange', newValues })
                  }}
                  onBuyNowPriceChange={() => setDidChangeBuyNowPrice(true)}
                  onShippingPriceChange={() => setDidChangeShippingPrice(true)}
                  onShippingMethodChange={() => setDidChangeShippingMethod(true)}
                  onClose={() => {
                    closeSideDrawer()
                    setEditingDrawerSection(null)
                  }}
                />
              </div>
            </Drawer>
          </div>
        </div>

        <div className="collapsed-drawer">
          {/* Remember to keep "dummy-collapsed-drawer-spacer" the same width as "collapsed-drawer" */}
          <Button
            className="drawer-open-button"
            data-testid="drawer-open-button"
            minimal
            icon={<Icon className="rotate-270" size={20} icon="export" color="#CED9E0" />}
            onClick={openSideDrawer}
          />
        </div>
      </div>
    </div>
  )
}

export default connect((state: AppState) => ({ current: state.current }))(ExpressListForm)
